import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Check, Star, Shield, Clock, MessageSquare, Zap, 
  Crown, ArrowRight, Home, MapPin, Euro, Users,
  TrendingUp, Heart, Sparkles, AlertCircle
} from 'lucide-react';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import api from '../services/api';

// Fake reviews for social proof
const REVIEWS = [
  {
    name: "Sophie",
    date: "2 feb 2026",
    rating: 5,
    avatar: "S",
    text: "Binnen 3 dagen een reactie gekregen! Super blij met Swipe je woning.",
    color: "bg-pink-500"
  },
  {
    name: "Thomas",
    date: "28 jan 2026", 
    rating: 5,
    avatar: "T",
    text: "Na maanden zoeken eindelijk mijn droomkamer gevonden. De premium was het meer dan waard!",
    color: "bg-blue-500"
  },
  {
    name: "Lisa",
    date: "15 jan 2026",
    rating: 5,
    avatar: "L", 
    text: "Verhuurders reageren veel sneller als je premium bent. Aanrader!",
    color: "bg-purple-500"
  },
  {
    name: "Mark",
    date: "10 jan 2026",
    rating: 5,
    avatar: "M",
    text: "Geweldige service. Binnen een week een kamer gevonden in Amsterdam.",
    color: "bg-green-500"
  },
  {
    name: "Emma",
    date: "5 jan 2026",
    rating: 5,
    avatar: "E",
    text: "De automatische berichten zijn top. Scheelt zoveel tijd!",
    color: "bg-orange-500"
  },
  {
    name: "Daan",
    date: "1 jan 2026",
    rating: 5,
    avatar: "D",
    text: "Snel en makkelijk. Veel beter dan andere sites.",
    color: "bg-teal-500"
  }
];

const PLANS = [
  {
    id: 'weekly',
    duration: '1 week',
    price: '€4,99',
    pricePerWeek: '€4,99',
    popular: true,
    discount: null,
    description: 'Perfect om snel te reageren'
  },
  {
    id: 'monthly',
    duration: '4 weken',
    price: '€14,99',
    pricePerWeek: '€3,75',
    popular: false,
    discount: '25%',
    description: 'Meest gekozen door actieve zoekers'
  },
  {
    id: 'quarterly',
    duration: '12 weken',
    price: '€29,99',
    pricePerWeek: '€2,50',
    popular: false,
    discount: '50%',
    description: 'Beste waarde voor lange zoektocht'
  }
];

const UpgradeForListingPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const [listing, setListing] = useState(location.state?.listing || null);
  const [loading, setLoading] = useState(!listing);
  const [selectedPlan, setSelectedPlan] = useState('weekly');
  const [processing, setProcessing] = useState(false);

  const isVerhuurder = user?.userType === 'verhuurder';

  // Redirect verhuurders - they cannot take a subscription
  useEffect(() => {
    if (isAuthenticated && isVerhuurder) {
      navigate('/verhuurder/dashboard');
    }
  }, [isAuthenticated, isVerhuurder, navigate]);

  useEffect(() => {
    if (!listing && listingId) {
      loadListing();
    }
  }, [listingId]);

  const loadListing = async () => {
    try {
      const response = await api.get(`/listings/${listingId}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error loading listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setProcessing(true);
    try {
      // Map plan to package_id
      const packageMap = {
        'weekly': 'swipematch_weekly',
        'monthly': 'swipematch_monthly', 
        'quarterly': 'swipematch_quarterly'
      };
      
      // Redirect to Stripe checkout with listing context
      const response = await api.post('/payments/checkout', {
        package_id: packageMap[selectedPlan] || 'swipematch_weekly',
        origin_url: window.location.origin,
        listing_id: listingId
      });
      
      if (response.data.url) {
        // Store listing ID to redirect after successful payment
        localStorage.setItem('upgrade_listing_id', listingId);
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      // Fallback to regular premium page
      navigate('/premium');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D2137] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full" />
      </div>
    );
  }

  const interestedCount = Math.floor(Math.random() * 15) + 8; // 8-22 fake interested users

  return (
    <div className="min-h-screen bg-[#0D2137]">
      {/* Header */}
      <div className="bg-[#0D2137] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <SwipeMatchLogo size="default" variant="white" />
          <Button 
            variant="ghost" 
            className="text-white/70 hover:text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            Terug
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <Badge className="bg-[#2E7D32] text-white mb-4 px-4 py-1">
            <Zap className="w-4 h-4 mr-1" />
            Premium vereist
          </Badge>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">
            Reageer op deze woning
          </h1>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Upgrade naar Premium om direct contact op te nemen met de verhuurder
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Listing Card */}
          <div>
            {/* Listing Preview */}
            <Card className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              {/* Listing Image */}
              <div className="relative">
                <img 
                  src={listing?.images?.[0] || listing?.image || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'} 
                  alt={listing?.title}
                  className="w-full h-48 md:h-56 object-cover"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <Badge className="bg-black/70 text-white backdrop-blur-sm">
                    {listing?.type || 'Kamer'}
                  </Badge>
                  {listing?.furnished && (
                    <Badge className="bg-[#2E7D32] text-white">Gemeubileerd</Badge>
                  )}
                </div>
                <div className="absolute bottom-3 right-3">
                  <Badge className="bg-red-500 text-white animate-pulse flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {interestedCount} geïnteresseerd
                  </Badge>
                </div>
              </div>

              {/* Listing Details */}
              <div className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h2 className="text-xl font-bold text-[#2A2A2A] mb-1">{listing?.title || 'Prachtige woning'}</h2>
                    <div className="flex items-center text-[#9E9E9E] text-sm">
                      <MapPin className="w-4 h-4 mr-1" />
                      {listing?.neighborhood && `${listing.neighborhood}, `}{listing?.city || 'Amsterdam'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-[#2E7D32]">€{listing?.price || '750'}</div>
                    <div className="text-xs text-[#9E9E9E]">per maand</div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="flex gap-4 text-sm text-[#9E9E9E] mb-4">
                  {listing?.size && <span>{listing.size}m²</span>}
                  {listing?.available && <span>Beschikbaar: {listing.available}</span>}
                </div>

                {/* Urgency Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Populaire woning!</p>
                    <p className="text-xs text-amber-600">
                      {interestedCount} anderen bekijken deze woning nu
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Benefits */}
            <div className="mt-6 bg-white/5 rounded-2xl p-5 border border-white/10">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                Wat krijg je met Premium?
              </h3>
              <div className="space-y-3">
                {[
                  { icon: MessageSquare, text: 'Direct berichten sturen naar verhuurders' },
                  { icon: Zap, text: 'Onbeperkt swipen op alle woningen' },
                  { icon: Star, text: 'Jouw profiel bovenaan bij verhuurders' },
                  { icon: Shield, text: 'Geverifieerde verhuurders badge' },
                  { icon: TrendingUp, text: '3x meer kans op reactie' }
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/80">
                    <div className="w-8 h-8 bg-[#2E7D32]/20 rounded-lg flex items-center justify-center">
                      <benefit.icon className="w-4 h-4 text-[#2E7D32]" />
                    </div>
                    <span className="text-sm">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Pricing */}
          <div>
            <Card className="bg-white rounded-2xl p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-[#2A2A2A] mb-2">Kies je abonnement</h3>
              <p className="text-[#9E9E9E] text-sm mb-6">Elk abonnement geeft volledige toegang</p>

              {/* Plans */}
              <div className="space-y-3 mb-6">
                {PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedPlan === plan.id 
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5' 
                        : 'border-[#E5E5E5] hover:border-[#2E7D32]/50'
                    }`}
                  >
                    {plan.popular && (
                      <Badge className="absolute -top-2 left-4 bg-[#2E7D32] text-white text-xs">
                        Meest gekozen
                      </Badge>
                    )}
                    {plan.discount && (
                      <Badge className="absolute -top-2 right-4 bg-red-500 text-white text-xs">
                        BESPAAR {plan.discount}
                      </Badge>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPlan === plan.id ? 'border-[#2E7D32] bg-[#2E7D32]' : 'border-[#9E9E9E]'
                        }`}>
                          {selectedPlan === plan.id && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <div className="font-bold text-[#2A2A2A]">{plan.duration}</div>
                          <div className="text-xs text-[#9E9E9E]">{plan.description}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-black text-[#2A2A2A]">{plan.price}</div>
                        <div className="text-xs text-[#9E9E9E]">{plan.pricePerWeek}/week</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button
                onClick={handleUpgrade}
                disabled={processing}
                className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-bold text-lg rounded-xl"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Bezig...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Upgrade & Reageer
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>

              {/* Trust Signals */}
              <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[#9E9E9E]">
                <span className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Veilig betalen
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Direct actief
                </span>
                <span>Op elk moment opzegbaar</span>
              </div>
            </Card>

            {/* Reviews */}
            <div className="mt-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                Wat anderen zeggen
              </h3>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {REVIEWS.map((review, i) => (
                  <Card key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${review.color} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}>
                        {review.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-white">{review.name}</span>
                          <span className="text-xs text-white/50">{review.date}</span>
                        </div>
                        <div className="flex gap-0.5 mb-2">
                          {[...Array(review.rating)].map((_, j) => (
                            <Star key={j} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <p className="text-sm text-white/70">{review.text}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: '500+', label: 'Huurders geholpen' },
                { value: '4.8/5', label: 'Gemiddelde score' },
                { value: '<24u', label: 'Gem. reactietijd' }
              ].map((stat, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                  <div className="text-xl font-black text-white">{stat.value}</div>
                  <div className="text-xs text-white/50">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 max-w-2xl mx-auto">
          <h3 className="text-white font-bold text-xl mb-6 text-center">Veelgestelde vragen</h3>
          <div className="space-y-3">
            {[
              { q: 'Kan ik direct reageren na betaling?', a: 'Ja! Je account wordt direct geactiveerd en je kunt meteen berichten sturen.' },
              { q: 'Hoe werkt opzeggen?', a: 'Je kunt op elk moment opzeggen via je profiel. Je houdt toegang tot het einde van je periode.' },
              { q: 'Is mijn betaling veilig?', a: 'Absoluut. We gebruiken Stripe voor veilige betalingen. Je gegevens zijn altijd beschermd.' }
            ].map((faq, i) => (
              <Card key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h4 className="font-semibold text-white mb-1">{faq.q}</h4>
                <p className="text-sm text-white/60">{faq.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeForListingPage;
